import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ArrowForward, CalendarMonth, CheckCircle, EnergySavingsLeaf, LocalFireDepartment, MenuBook, Spa, WbSunny } from '@mui/icons-material';
import api from '../utils/api';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import HomepageSlideshow from '../components/HomepageSlideshow';

const palette = {
  saffron: '#d9672b',
  orange: '#ee8d42',
  gold: '#c88a2d',
  ink: '#382b25',
  cream: '#fffaf1',
  muted: '#796a60'
};

const pathashalas = [
  { name: 'Krishna Yajurveda', subtitle: 'कृष्ण यजुर्वेदः', icon: LocalFireDepartment, total: 128, completed: 92, tone: '#d9672b', ring: true },
  { name: 'Lakshana', subtitle: 'लक्षण पाठशाला', icon: WbSunny, total: 86, completed: 61, tone: '#c88a2d', ring: false },
  { name: 'Sahitya', subtitle: 'साहित्य पाठशाला', icon: EnergySavingsLeaf, total: 64, completed: 48, tone: '#75945b', ring: false },
  { name: 'Shastra', subtitle: 'शास्त्र पाठशाला', icon: MenuBook, total: 72, completed: 42, tone: '#a66349', ring: true }
];

const labels = {
  telugu: {
    welcome: 'వేదారణ్యానికి స్వాగతం',
    eyebrow: 'సనాతన విద్యా పరంపర',
    intro: 'ఆధ్యాత్మిక స్పర్శతో యుగయుగాలుగా భారతదేశాన్ని మార్గదర్శనం చేసిన సనాతన వైదిక వాంగ్మయానికి పునర్వైభవాన్ని కలిగించి, దాని మహిమను విస్తృతంగా ప్రచారం చేయాలనే ధ్యేయంతో ఈ సంస్థ అంకితభావంతో సేవలందిస్తోంది. ఆ ధ్యేయంలో భాగంగా ఉభయ తెలుగు రాష్ట్ర విద్యార్థులకు కృష్ణ యజుర్వేద/లక్షణ/సాహిత్య/శాస్త్ర వార్షిక మరియూ పట్టా పరీక్షలు నిర్వహించబడుతున్నాయి.',
    home: 'హోమ్',
    pathasala: 'పాఠశాల నిర్వహణ',
    exams: 'పరీక్షా విభాగం',
    cta: 'నమోదు ప్రారంభించండి',
    overview: 'పాఠశాలల అవలోకనం',
    overviewText: 'మీ వేద విద్యా కేంద్రాల ప్రస్తుత పురోగతి',
    total: 'మొత్తం',
    completed: 'పూర్తయినవి',
    pending: 'పెండింగ్',
    viewAll: 'అన్ని పాఠశాలలు చూడండి',
    registration: 'నమోదు గడువు',
    learn: 'వివరాలు చూడండి'
  },
  sanskrit: {
    welcome: 'वेदारण्यं नियन्त्रणपटलम्',
    eyebrow: 'सनातनविद्यापरम्परा',
    intro: 'वेदज्ञानस्य संरक्षणं कुर्वन्तः सर्वेषां पाठशालानां प्रगतिं एकत्र प्रबन्धयन्तु।',
    home: 'गृहम्',
    pathasala: 'पाठशाला व्यवस्थापनम्',
    exams: 'परीक्षा विभागः',
    cta: 'पञ्जीकरणं प्रारभताम्',
    overview: 'पाठशालानाम् अवलोकनम्',
    overviewText: 'भवतः वैदिकशिक्षाकेन्द्राणां वर्तमान प्रगतिः',
    total: 'सम्पूर्णम्',
    completed: 'पूर्णम्',
    pending: 'अवशिष्टम्',
    viewAll: 'सर्वाः पाठशालाः पश्यन्तु',
    registration: 'पञ्जीकरणस्य अन्तिमदिनम्',
    learn: 'विवरणं पश्यन्तु'
  },
  english: {
    welcome: 'Vedaaranyam Dashboard',
    eyebrow: 'Sanatana Learning Tradition',
    intro: 'Preserve Vedic knowledge and manage the progress of every pathashala in one place.',
    home: 'Home',
    pathasala: 'Pathashala Management',
    exams: 'Exam Section',
    cta: 'Start Registration',
    overview: 'Pathashala Overview',
    overviewText: 'Current progress across your Vedic learning centres',
    total: 'Total',
    completed: 'Completed',
    pending: 'Pending',
    viewAll: 'View All Pathashalas',
    registration: 'Registration deadline',
    learn: 'View details'
  }
};

function ProgressRing({ value, color }) {
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <Box sx={{ position: 'relative', width: 76, height: 76, flexShrink: 0 }}>
      <svg width="76" height="76" viewBox="0 0 76 76" aria-label={`${value}% complete`}>
        <circle cx="38" cy="38" r={radius} fill="none" stroke="#f4eadc" strokeWidth="7" />
        <circle
          cx="38"
          cy="38"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 38 38)"
        />
      </svg>
      <Typography sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: palette.ink, fontWeight: 800, fontSize: '0.95rem' }}>
        {value}%
      </Typography>
    </Box>
  );
}

function PathashalaCard({ item, copy, onOpen }) {
  const Icon = item.icon;
  const percentage = Math.round((item.completed / item.total) * 100);

  return (
    <Box
      component="article"
      sx={{
        backgroundColor: '#fff',
        border: '1px solid rgba(200, 138, 45, 0.12)',
        borderRadius: '20px',
        p: { xs: 2, sm: 2.5 },
        boxShadow: '0 14px 35px rgba(201, 126, 53, 0.10)',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 18px 42px rgba(201, 126, 53, 0.17)' }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '14px', display: 'grid', placeItems: 'center', color: item.tone, backgroundColor: `${item.tone}16`, flexShrink: 0 }}>
            <Icon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: palette.ink, fontWeight: 800, fontSize: '1rem', lineHeight: 1.25 }}>{item.name}</Typography>
            <Typography sx={{ color: palette.muted, fontSize: '0.78rem', mt: 0.35 }}>{item.subtitle}</Typography>
          </Box>
        </Box>
        {item.ring && <ProgressRing value={percentage} color={item.tone} />}
      </Box>

      <Box sx={{ mt: 2.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', color: palette.muted, fontSize: '0.78rem', mb: 0.8 }}>
          <span>{copy.completed}</span>
          <strong style={{ color: palette.ink }}>{item.completed} / {item.total}</strong>
        </Box>
        {!item.ring && (
          <Box sx={{ height: 8, borderRadius: 99, backgroundColor: '#f4eee7', overflow: 'hidden' }}>
            <Box sx={{ width: `${percentage}%`, height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${item.tone}, ${palette.orange})` }} />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1.25, color: palette.muted, fontSize: '0.73rem' }}>
          <span>{copy.total}: {item.total}</span>
          <span>{copy.pending}: {item.total - item.completed}</span>
        </Box>
      </Box>

      <Button
        onClick={onOpen}
        endIcon={<ArrowForward sx={{ fontSize: '1rem !important' }} />}
        sx={{ mt: 1.7, p: 0, minWidth: 0, color: item.tone, fontSize: '0.78rem', fontWeight: 800, textTransform: 'none', '&:hover': { background: 'transparent', color: palette.saffron } }}
      >
        {copy.learn}
      </Button>
    </Box>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { teluguYearTelugu, teluguYearSanskrit } = useTeluguYear();
  const copy = labels[lang] || labels.sanskrit;
  const [registrationsEnd, setRegistrationsEnd] = useState('');

  useEffect(() => {
    let active = true;
    const fetchActiveConfig = async () => {
      try {
        const response = await api.get('/api/admin-config/active');
        const data = await response.json();
        if (active && response.ok && data.success && data.data) setRegistrationsEnd(data.data.registrations_end || '');
      } catch (error) {
        console.error('Error fetching active admin config:', error);
      }
    };
    fetchActiveConfig();
    return () => { active = false; };
  }, []);

  const formatRegistrationDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return dateStr || '';
    const [day, month, year] = dateStr.split(/[-/]/).map(value => value.trim());
    const monthNumber = Number(month);
    if (!day || !year || Number.isNaN(monthNumber)) return dateStr;
    const monthNames = lang === 'telugu'
      ? ['జనవరి', 'ఫిబ్రవరి', 'మార్చి', 'ఏప్రిల్', 'మే', 'జూన్', 'జులై', 'ఆగస్టు', 'సెప్టెంబర్', 'అక్టోబర్', 'నవంబర్', 'డిసెంబర్']
      : lang === 'sanskrit'
        ? ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवम्बर', 'दिसम्बर']
        : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${day} ${monthNames[monthNumber - 1] || month} ${year}`;
  };

  const registrationDate = formatRegistrationDate(registrationsEnd);
  const yearLabel = lang === 'telugu' ? teluguYearTelugu : lang === 'sanskrit' ? teluguYearSanskrit : '';

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', background: 'linear-gradient(145deg, #fffdf8 0%, #fff5e8 50%, #f9e3cb 100%)', color: palette.ink, pb: { xs: 5, md: 8 } }}>
      <Box sx={{ position: 'relative', height: { xs: 340, md: 290 }, mt: 0, overflow: 'hidden', boxShadow: '0 18px 40px rgba(131, 76, 35, 0.15)' }}>
        <Box component="img" src="/assets/Gurukulam.jpeg" alt="Gurukulam sunrise placeholder" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(47,25,11,0.76) 0%, rgba(47,25,11,0.22) 56%, rgba(47,25,11,0.04) 100%), linear-gradient(0deg, #fff8ed 0%, transparent 30%)' }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 580, p: { xs: 3, sm: 4, md: 3.5 }, pt: { xs: 5, md: 3.5 } }}>
          <Typography sx={{ color: '#ffd88a', fontWeight: 800, letterSpacing: 2, fontSize: '0.72rem', textTransform: 'uppercase' }}>{copy.eyebrow}</Typography>
          <Typography component="h4" sx={{ color: '#fffaf1', fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: { xs: '1.15rem', sm: '1.6rem', md: '1.5rem' }, lineHeight: 1.08, mt: 1.1 }}>{copy.welcome}</Typography>
          <Typography sx={{ color: 'rgba(255,250,241,0.88)', fontSize: { xs: '0.9rem', md: '0.82rem' }, lineHeight: 1.5, mt: 5.1, maxWidth: 500 }}>{copy.intro}</Typography>
          {/* <Button onClick={() => navigate('/pathasalaMgmt')} endIcon={<ArrowForward />} sx={{ mt: 2.5, color: palette.ink, bgcolor: '#ffe1a7', borderRadius: 99, px: 2.2, py: 1, textTransform: 'none', fontWeight: 800, '&:hover': { bgcolor: '#fff0cc' } }}>{copy.viewAll}</Button> */}
        </Box>
      </Box>
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3, mt: { xs: 2.5, md: 3.5 }, px: { xs: 1.5, md: 2 }, py: 1.4, borderRadius: '14px', backgroundColor: 'rgba(255,255,255,0.64)', border: '1px solid rgba(200,138,45,0.14)' }}>
          <CalendarMonth sx={{ color: palette.saffron, fontSize: 21 }} />
          <Typography sx={{ color: palette.muted, fontSize: '0.82rem' }}>{copy.registration}:</Typography>
          <Typography sx={{ color: palette.ink, fontWeight: 800, fontSize: '0.85rem' }}>{registrationDate || '—'}</Typography>
          <Typography sx={{ display: { xs: 'none', sm: 'block' }, ml: 'auto', color: palette.gold, fontSize: '0.78rem', fontWeight: 700 }}>{yearLabel || ''}</Typography>
        </Box>

        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <HomepageSlideshow />
        </Box>

        <Box sx={{ mt: { xs: 4, md: 5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', mb: 2 }}>
            <Box>
              <Typography sx={{ color: palette.saffron, fontWeight: 800, fontSize: '0.73rem', letterSpacing: 1.5, textTransform: 'uppercase' }}>{copy.eyebrow}</Typography>
              <Typography component="h2" sx={{ color: palette.ink, fontWeight: 900, fontSize: { xs: '1.55rem', md: '2rem' }, mt: 0.5 }}>{copy.overview}</Typography>
              <Typography sx={{ color: palette.muted, fontSize: '0.86rem', mt: 0.5 }}>{copy.overviewText}</Typography>
            </Box>
            <Spa sx={{ display: { xs: 'none', sm: 'block' }, color: '#e5b05a', fontSize: 42 }} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: { xs: 1.5, md: 2 } }}>
            {pathashalas.map(item => <PathashalaCard key={item.name} item={item} copy={copy} onOpen={() => navigate('/pathasalaMgmt')} />)}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8, mt: 4, color: palette.muted, fontSize: '0.73rem' }}>
          <CheckCircle sx={{ color: '#77935b', fontSize: 17 }} />
          <span>{lang === 'telugu' ? 'వేద విద్యా పరంపరను కలిసి ముందుకు తీసుకెళ్దాం' : 'वेदविद्यापरम्परां मिलित्वा अग्रे नयामः'}</span>
        </Box>
      </Box>
    </Box>
  );
}
